/**
 * Animation Visibility Hook
 *
 * Manages enter/exit animations with proper render timing.
 * Handles the common pattern of:
 * 1. Visible becomes true → start render, play enter animation
 * 2. Visible becomes false → play exit animation, then stop render
 */

import { useEffect, useState } from "preact/hooks";
import { exitAnimationDuration } from "../utils/constants";

interface UseAnimationVisibilityOptions {
  /** Duration of exit animation in milliseconds */
  exitDuration?: number;
}

interface UseAnimationVisibilityResult {
  /** Whether the component should render (stays true during exit animation) */
  shouldRender: boolean;
  /** Whether the component is in exit animation state */
  isExiting: boolean;
}

/**
 * Hook to manage visibility with enter/exit animations.
 *
 * `shouldRender` is derived synchronously from `visible` (plus a bookkeeping
 * flag that keeps the component mounted while the exit animation plays). This
 * avoids a one-render dead cycle where a component previously hidden would
 * briefly return null on the render that flips `visible` back to true.
 *
 * @example
 * const { shouldRender, isExiting } = useAnimationVisibility(visible);
 *
 * if (!shouldRender) return null;
 *
 * const animationClass = isExiting ? "pw:animate-slideDown" : "pw:animate-slideUp";
 */
export function useAnimationVisibility(
  visible: boolean,
  options: UseAnimationVisibilityOptions = {},
): UseAnimationVisibilityResult {
  const { exitDuration = exitAnimationDuration } = options;

  // True while the exit animation is still playing after `visible` flipped
  // false. Goes false once the timer clears, which unmounts the component.
  const [retainForExit, setRetainForExit] = useState<boolean>(visible);

  useEffect((): (() => void) | undefined => {
    if (visible) {
      setRetainForExit(true);
      return undefined;
    }
    if (retainForExit) {
      const timer = setTimeout((): void => {
        setRetainForExit(false);
      }, exitDuration);
      return (): void => clearTimeout(timer);
    }
    return undefined;
  }, [visible, retainForExit, exitDuration]);

  return {
    shouldRender: visible || retainForExit,
    isExiting: !visible && retainForExit,
  };
}
